/**
 * RichText — tiny safe inline-markdown renderer:
 *   **bold**, __bold__, *italic*, _italic_, `code`, plus newline → <br/>.
 * Designed for use inside sheet cells / table cells (no block-level parsing).
 *
 * ExpandableText — wraps RichText with a smart "View more" affordance:
 *   - Collapsed: max 3 lines (line-clamp) OR `limit` chars (whichever hits first), then "View more".
 *   - Expanded: opens a side Dialog with the FULL content. Read-only or editable based on `canEdit`.
 */
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

// SAFE inline-markdown → JSX. No raw HTML escapes — we only emit plain text fragments
// and elements we construct ourselves, so XSS is not possible.
export function renderInlineMd(text) {
  if (text == null) return null;
  const s = String(text);
  if (!s) return s;
  // Pattern order matters (longest first so __ wins over _ and ** wins over *)
  const tokens = [];
  let i = 0;
  const push = (node) => tokens.push(node);
  while (i < s.length) {
    // newline → <br>
    if (s[i] === "\n") { push(<br key={`br-${i}`} />); i++; continue; }
    // **bold** or __bold__
    let m = s.slice(i).match(/^(\*\*|__)(.+?)\1/);
    if (m) {
      push(<strong key={`b-${i}`} className="font-semibold text-white">{renderInlineMd(m[2])}</strong>);
      i += m[0].length; continue;
    }
    // *italic* or _italic_  (single, not adjacent to other letters to avoid eating snake_case)
    m = s.slice(i).match(/^([*_])([^*_\s].*?[^*_\s]|[^*_\s])\1/);
    if (m) {
      push(<em key={`i-${i}`} className="italic">{renderInlineMd(m[2])}</em>);
      i += m[0].length; continue;
    }
    // `inline code`
    m = s.slice(i).match(/^`([^`]+)`/);
    if (m) {
      push(<code key={`c-${i}`} className="px-1 py-px text-[11px] font-mono bg-zinc-800/80 rounded text-zinc-100">{m[1]}</code>);
      i += m[0].length; continue;
    }
    // Plain run — gobble until the next markdown trigger
    const next = s.slice(i).search(/[\n*_`]/);
    const chunk = next === -1 ? s.slice(i) : s.slice(i, i + (next || 1));
    push(chunk);
    i += chunk.length;
  }
  return tokens;
}

const DEFAULT_CHAR_LIMIT = 90;

export function ExpandableText({
  value,
  canEdit = false,
  onCommit,
  placeholder = "—",
  charLimit = DEFAULT_CHAR_LIMIT,
  className = "",
  testId,
  title = "Full text",
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const v = value ?? "";
  // Trigger "View more" purely on char length so the column width stays predictable.
  // No -webkit-line-clamp ellipsis — we hard-slice the visible portion.
  const isLong = v.length > charLimit;

  const openDialog = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setDraft(v);
    setOpen(true);
  };

  const save = () => {
    if (onCommit && draft !== v) onCommit(draft);
    setOpen(false);
  };

  // Hard slice without trailing ellipsis, prefer breaking on whitespace.
  const trimmed = (() => {
    if (!isLong) return v;
    const cut = v.slice(0, charLimit);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > charLimit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[,.;:\-\u2014\u2013]\s*$/, "");
  })();

  return (
    <>
      <div
        className={`whitespace-pre-wrap break-words text-zinc-200 ${className}`}
        data-testid={testId}
      >
        {!v ? <span className="text-zinc-600">{placeholder}</span> : isLong ? (
          <>
            <span
              className="block overflow-hidden"
              style={{ maxHeight: "3.6em", lineHeight: "1.2em" }}
            >
              {renderInlineMd(trimmed)}
            </span>
            <button
              type="button"
              onClick={openDialog}
              className="text-[11px] text-zinc-400 hover:text-white underline-offset-2 hover:underline font-mono align-baseline mt-0.5 inline-block"
              data-testid="expand-text-btn"
            >
              View more →
            </button>
          </>
        ) : renderInlineMd(v)}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111] border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="expand-text-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{title}</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 font-mono">
              {canEdit
                ? "Edit freely — supports **bold**, *italic*, `code` and line breaks."
                : "Read-only · ask your team to edit"}
            </DialogDescription>
          </DialogHeader>
          {canEdit ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={14}
              autoFocus
              className="w-full bg-[#0a0a0a] border border-zinc-800 rounded-md px-3 py-2 text-[13px] text-zinc-100 outline-none resize-vertical font-mono leading-relaxed"
              data-testid="expand-text-textarea"
            />
          ) : (
            <div
              className="whitespace-pre-wrap break-words text-zinc-200 leading-relaxed text-[13px] max-h-[55vh] overflow-y-auto"
              data-testid="expand-text-readonly"
            >
              {renderInlineMd(v)}
            </div>
          )}
          {canEdit && (
            <div className="text-[10px] text-zinc-700 font-mono">
              Cmd/Ctrl + B inserts <b>**bold**</b>; Cmd/Ctrl + I inserts <em>*italic*</em>. Or type the markers directly.
            </div>
          )}
          <DialogFooter className="gap-2">
            {canEdit ? (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 h-9 text-[12px]" data-testid="expand-text-cancel">Cancel</Button>
                <Button onClick={save} className="bg-white hover:bg-zinc-200 text-black h-9 text-[12px]" data-testid="expand-text-save">Save</Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)} className="bg-white hover:bg-zinc-200 text-black h-9 text-[12px]" data-testid="expand-text-close">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
